import { useCallback, useMemo } from "react";
import { departmentTypesForApi, useGetAllUserDepartmentQuery } from "../../api/user/user-api";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { DepartmentUser, DepartmentUsersResponse } from "../../api/user/user-api-state";
import { DropdownOption } from "../../features/auth/auth-state";
import { useMyTeamDataState } from "../data-state/use-my-team-data-state";

interface Props {
    departmentType: departmentTypesForApi;
}

export const useDepartmentUserAccess = ({ departmentType }: Props) => {

    const { searchValueMyTeam } = useMyTeamDataState()
    const { 
        data: departmentUsers, 
        isLoading: isLoadingDepartmentUsers, 
        isError: isErrorDepartmentUsers, 
        error: errorDepartmentUsers, 
        refetch: refetchDepartmentUsers, 
        isFetching: isFetchingDepartmentUsers 
    } = useGetAllUserDepartmentQuery({type: departmentType});
    const users = departmentUsers?.data.users ?? [];
    const errorMessageDepartmentUsers = getErrorMessage(errorDepartmentUsers) 

    const filteredUsers = useMemo(() => {
        const search = searchValueMyTeam.trim().toLowerCase();

        if (!search) return users;

        return users.filter((user: DepartmentUser) => {
            const searchableFields = [
                user.name,
                user.firstname,
                user.middlename,
                user.lastname,
                user.suffix,
                user.email,
                // user.agent_code,
                user.is_active ? "active" : "inactive",
                user.department_name,
                user.location_name,
                user.phoneNumber,
                // user.position,
            ];

            return searchableFields.some((field) =>
                String(field ?? "").toLowerCase().includes(search)
            );
        });
    }, [users, searchValueMyTeam]);

    const formattedUser = useMemo(() => (users).map((user: DepartmentUser) => ({
        id: user.user_id,
        name: user.name,
        email: user.email,
    })), [departmentUsers?.data.users]);

    const formattedUserInActive = useMemo(() => (users)
        .filter((user: DepartmentUser) => user.is_active === false),
        [departmentUsers?.data.users]
    );

    const formattedUserActive = useMemo(() => (users)
        .filter((user: DepartmentUser) => user.is_active === false),
        [departmentUsers?.data.users]
    );

    const formattedUserInActiveOption: DropdownOption[] = useMemo(() => (users)
        .filter((user: DepartmentUser) => user.is_active === false)
        .map((user: DepartmentUser) => ({
            id: user.user_id,
            name: user.name,
            subTitle: user.email,
    })), [departmentUsers?.data.users]);

    const formattedUserActiveOption: DropdownOption[] = useMemo(() => (users)
        .filter((user: DepartmentUser) => user.is_active === true)
        .map((user: DepartmentUser) => ({
            id: user.user_id,
            name: user.name,
            subTitle: user.email,
    })), [departmentUsers?.data.users]);

    const getDepartmentUserById = useCallback((userId: number): DepartmentUser | undefined =>
            users.find((user: DepartmentUser) => user.user_id === userId),
        [users]
    );

    // const user = getDepartmentUserById(123);

    return {
        departmentUsers: users,
        departmentCount: departmentUsers?.data.count,
        isLoadingDepartmentUsers,
        isErrorDepartmentUsers,
        errorDepartmentUsers,
        errorMessage: errorMessageDepartmentUsers,

        formattedUserInActive,
        formattedUserActive,

        formattedUser,
        formattedUserInActiveOption,
        formattedUserActiveOption,

        getDepartmentUserById,

        filteredUsers,
    }
}